import type {
  EquipmentAvailabilityInput,
  EquipmentAvailabilityOutput,
  EquipmentPlace,
  MeasurementInput,
  ProfileInput,
  ProfileOutput,
  TrainingConfigInput,
  TrainingConfigOutput,
} from "@repo/contract/training";

/**
 * Form state → request body, for the section endpoints whose editors are
 * plain fields. Every function here returns an UNVALIDATED body: the caller
 * runs it through the contract schema (`validate.ts`) before sending, so a
 * missing required field is a field error, not a `400`.
 */

// ── PUT /me/profile ─────────────────────────────────────────────────────────

/**
 * The stored profile plus one edit.
 *
 * `dateOfBirth`, `heightCm` and `sex` are required on every `PUT`, so they
 * come from what is stored. The seven optional facts (`motivation` and the
 * lifestyle ones) are NOT copied: absent leaves what is stored alone
 * (profile.ts, `D148`), so only the edited one travels. With no stored
 * profile the required fields are simply missing and the schema says so.
 */
export function profileInputFrom(
  current: ProfileOutput | null,
  patch: Partial<ProfileInput>
): Partial<ProfileInput> {
  const base: Partial<ProfileInput> =
    current === null
      ? {}
      : {
          dateOfBirth: current.dateOfBirth,
          heightCm: current.heightCm,
          sex: current.sex,
        };
  return { ...base, ...patch };
}

// ── PUT /me/training-config ─────────────────────────────────────────────────

const TRAINING_CONFIG_KEYS = [
  "daysFixed",
  "daysPerWeek",
  "dominantSide",
  "experience",
  "fixedWeekdays",
  // Copied only when the server echoed it — an older one never sees the key
  // its strict input would refuse (`D216`, `D172`).
  "preferredSplit",
  "primaryGoal",
  "secondaryGoal",
  "sessionMinutes",
  "sessionMinutesIsHardLimit",
] as const satisfies readonly (keyof TrainingConfigInput)[];

/**
 * The WHOLE strict object, built from the stored config plus the edit.
 * `trainingConfigInput` is a `strictObject` whose fields are required, so a
 * partial body is a `400`; and the output carries `level` and `updatedAt`,
 * which the input refuses, so the stored value is picked key by key, never
 * spread.
 *
 * A new primary goal equal to the stored secondary clears the secondary
 * (A2 editors.tsx:223-227) — otherwise the contract's "secondaryGoal must
 * differ" refinement refuses a save the person had no way to see coming.
 */
export function trainingConfigInputFrom(
  current: TrainingConfigOutput | null,
  patch: Partial<TrainingConfigInput>
): Partial<TrainingConfigInput> {
  const next: Record<string, unknown> = {};
  if (current !== null) {
    for (const key of TRAINING_CONFIG_KEYS) {
      if (current[key] !== undefined) {
        next[key] = current[key];
      }
    }
  }
  Object.assign(next, patch);
  if (
    patch.primaryGoal !== undefined &&
    patch.secondaryGoal === undefined &&
    next.secondaryGoal === patch.primaryGoal
  ) {
    next.secondaryGoal = null;
  }
  return next as Partial<TrainingConfigInput>;
}

// ── PUT /me/equipment ───────────────────────────────────────────────────────

/**
 * The checklist's body: `available` and NOTHING else.
 *
 * `place` absent leaves the stored place alone; `null` clears it (contracts
 * §3). A checklist save that spread `place: current ?? null` would wipe the
 * place on every save, so the key is never written here.
 */
export function checklistInput(
  available: readonly number[]
): EquipmentAvailabilityInput {
  return { available: [...new Set(available)] };
}

/**
 * The place control's body: the place, and the stored checklist unchanged
 * (the `PUT` replaces `available` too). With nothing stored yet the list is
 * empty, which the server accepts (`{ available: [], place }`, T29).
 */
export function placeInput(
  current: EquipmentAvailabilityOutput | null,
  place: EquipmentPlace | null
): EquipmentAvailabilityInput {
  return { available: [...(current?.available ?? [])], place };
}

// ── POST /me/measurements ───────────────────────────────────────────────────

/** The two series the body editors write (`body-measurement.ts` kinds). */
export const MEASUREMENT_KINDS = {
  bodyFat: { kind: "body_fat", unit: "%" },
  weight: { kind: "weight", unit: "kg" },
} as const;

export type MeasurementEditor = keyof typeof MEASUREMENT_KINDS;

/**
 * One new point. Append-only (`D68`): a correction is a new row plus a
 * `DELETE` of the wrong one, never a second `POST`. The idempotency key is the
 * caller's, minted once per ATTEMPT and resent on a retry of it (`D154`), so
 * a lost response does not append a twin.
 */
export function measurementInput(
  editor: MeasurementEditor,
  value: number,
  idempotencyKey: string
): MeasurementInput {
  const { kind, unit } = MEASUREMENT_KINDS[editor];
  return { idempotencyKey, kind, unit, value };
}

// ── The birth date ──────────────────────────────────────────────────────────

export interface DayParts {
  readonly d: number;
  readonly m: number;
  readonly y: number;
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` into its parts, read as a calendar day (no time zone). */
export function dayParts(day: string): DayParts | null {
  const match = CALENDAR_DAY.exec(day);
  if (!match) {
    return null;
  }
  return { d: Number(match[3]), m: Number(match[2]), y: Number(match[1]) };
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** The parts as `YYYY-MM-DD`, the day clamped to the month's length. */
export function calendarDayOf(parts: DayParts): string {
  const d = Math.min(parts.d, daysInMonth(parts.y, parts.m));
  return `${String(parts.y).padStart(4, "0")}-${String(parts.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Whole years between a birth day and `today` (both calendar days). */
export function ageOn(birth: DayParts, today: DayParts): number {
  const hadBirthday =
    today.m > birth.m || (today.m === birth.m && today.d >= birth.d);
  return today.y - birth.y - (hadBirthday ? 0 : 1);
}
